import { useAppSelector } from 'hooks/reduxHooks'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LinePhoneAltIcon from 'LuxKit/Icons/line/LinePhoneAltIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { isCarHireInsuranceAvailable } from 'selectors/carHireSelectors'
import type { CarsValueProposition } from '../CarsValuePropositions'

function useCarHireValuePropositions(): Array<CarsValueProposition> {
  const intl = useIntl()
  const carHireInsuranceAvailable = useAppSelector(isCarHireInsuranceAvailable)

  return useMemo(() => {
    const freeCancellationValue: CarsValueProposition = {
      id: 'free-cancellation',
      icon: LineCheckCircleIcon,
      title: intl.formatMessage({
        id: 'l8M8YwNh5lKsSrxyy9Ia',
        defaultMessage: 'Free cancellation',
        description: 'USP title for free cancellation on car hire page in the car hire section',
      }),
      description: intl.formatMessage({
        id: 'cqyiSzsI6IlZHaEvRcXJ',
        defaultMessage: 'We offer free cancellation up to 48 hours before your pick-up time.',
        description: 'USP description for free cancellation on car hire page',
      }),
    }
    const supportValue: CarsValueProposition = {
      id: 'support',
      icon: LinePhoneAltIcon,
      title: intl.formatMessage({
        id: 'yRn0cJ47Vuh9W04J7uZX',
        defaultMessage: '24/7 Support service',
        description: 'USP title for 24/7 support on car hire page in car hire section',
      }),
      description: intl.formatMessage({
        id: 'clFYvsgNdug3dbRnZDA4',
        defaultMessage: 'Our support team is always on standby, available 24/7.',
        description: 'USP description for 24/7 support on car hire page in the car hire section',
      }),
    }

    let middleValue: CarsValueProposition = {
      id: 'trusted-rental-partners',
      icon: LineShieldCheckIcon,
      title: intl.formatMessage({
        id: 'FrZMOSBK8OxwtPL8ylmm',
        defaultMessage: 'Trusted rental partners',
        description: 'USP title for trusted partners on car hire page in the car hire section',
      }),
      description: intl.formatMessage({
        id: 'oujLSjO9BGTrSGDc4BeX',
        defaultMessage: 'Discover an extensive range of vehicles from our trusted suppliers.',
        description: 'USP description for trusted partners on car hire page',
      }),
    }

    if (carHireInsuranceAvailable) {
      middleValue = {
        id: 'peace-of-mind-insurance',
        icon: LineShieldCheckIcon,
        title: intl.formatMessage({
          id: 'Az4pTGDCPjCEJAOm4RGq',
          defaultMessage: 'Peace of mind insurance',
          description: 'USP title for insurance on car hire page in the insurance flow',
        }),
        description: intl.formatMessage({
          id: 'LA-7f1o3Q5DuvJ-YUgJy',
          defaultMessage: 'We can provide complete protection insurance options without any excess fees at a great price.',
          description: 'USP description for insurance on car hire page in the car hire section',
        }),
      }
    }

    return [freeCancellationValue, middleValue, supportValue]
  }, [carHireInsuranceAvailable, intl])
}

export default useCarHireValuePropositions
